import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Waves } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';

/**
 * Capital Flow — animated Sankey-style SVG diagram
 * Columns (left → right):
 *   1. Initial Capital ($X)
 *   2. 3 Baskets (DCA Bitcoin & Crypto, AI Trading, Web3)
 *   3. AI sub-split (Sigmai, Sugair, Jairvis) — only from AI basket
 *   4. Final Value (1y, 3y, 5y)
 */
const CapitalFlow = ({
  totalCapital,
  monthlyContribution = 0,
  allocations,
  aiSplit,
  projections,
}) => {
  const { t } = useLanguage();
  const [hovered, setHovered] = useState(null);

  // Canvas dimensions
  const W = 1100;
  const H = 520;
  const PAD_X = 20;
  const PAD_Y = 30;

  // Column x positions
  const col = {
    capital: 100,
    basket: 420,
    bot: 720,
    fv: 1020,
  };

  const formatCurrency = (amount) => {
    if (!isFinite(amount)) return '$0';
    if (Math.abs(amount) >= 1e6) return `$${(amount / 1e6).toFixed(2)}M`;
    if (Math.abs(amount) >= 1e3) return `$${(amount / 1e3).toFixed(1)}K`;
    return `$${Math.round(amount)}`;
  };

  // Extract allocations with backward compat
  const dcaAlloc =
    allocations.dcaCrypto !== undefined
      ? allocations.dcaCrypto
      : (allocations.bitcoin || 0) + (allocations.diversified || 0);
  const aiAlloc = allocations.aiTrading || 0;
  const web3Alloc = allocations.web3 || 0;

  const sigmaiPct = aiSplit.sigmai || 0;
  const sugairPct = aiSplit.sugair || 0;
  const jairvisPct =
    aiSplit.jairvis !== undefined ? aiSplit.jairvis : aiSplit.jarvis || 0;

  // Total invested over the longest horizon (5y)
  const totalInvested = totalCapital + monthlyContribution * 60;

  // Basket invested amounts (based on totalInvested distribution)
  const dcaInvested = totalInvested * (dcaAlloc / 100);
  const aiInvested = totalInvested * (aiAlloc / 100);
  const web3Invested = totalInvested * (web3Alloc / 100);

  // AI bot invested
  const sigmaiInvested = aiInvested * (sigmaiPct / 100);
  const sugairInvested = aiInvested * (sugairPct / 100);
  const jairvisInvested = aiInvested * (jairvisPct / 100);

  // Final values from projections (5y primarily)
  const p5y = projections[1825] || {};
  const p3y = projections[1095] || {};
  const p1y = projections[365] || {};

  const fv5y = p5y.total || 0;
  const fv3y = p3y.total || 0;
  const fv1y = p1y.total || 0;

  // Basket node heights (proportional to allocation %)
  const maxBarH = 360;
  const dcaH = Math.max(24, (dcaAlloc / 100) * maxBarH);
  const aiH = Math.max(24, (aiAlloc / 100) * maxBarH);
  const web3H = Math.max(24, (web3Alloc / 100) * maxBarH);
  const totalH = dcaH + aiH + web3H;
  const baseY = (H - totalH) / 2;

  // Basket Y centers
  const dcaY = baseY + dcaH / 2;
  const aiY = baseY + dcaH + aiH / 2;
  const web3Y = baseY + dcaH + aiH + web3H / 2;

  // AI sub-split positions (around aiY)
  const botMaxH = aiH;
  const sigmaiBotH = Math.max(12, (sigmaiPct / 100) * botMaxH);
  const sugairBotH = Math.max(12, (sugairPct / 100) * botMaxH);
  const jairvisBotH = Math.max(12, (jairvisPct / 100) * botMaxH);
  const botTotalH = sigmaiBotH + sugairBotH + jairvisBotH;
  const botBaseY = aiY - botTotalH / 2;

  const sigmaiY = botBaseY + sigmaiBotH / 2;
  const sugairY = botBaseY + sigmaiBotH + sugairBotH / 2;
  const jairvisY = botBaseY + sigmaiBotH + sugairBotH + jairvisBotH / 2;

  // FV column positions — split into 1y / 3y / 5y stacked
  const fvCenterY = H / 2;
  const fvY1 = fvCenterY - 120;
  const fvY3 = fvCenterY;
  const fvY5 = fvCenterY + 120;

  // Node colors (must match brand)
  const COLORS = {
    capital: '#00f4ff',
    dca: '#f7931a', // bitcoin orange
    ai: '#00f4ff',
    web3: '#12b7ff',
    sigmai: '#ff3030',
    sugair: '#22dd55',
    jairvis: '#00f4ff',
    fv1y: '#12b7ff',
    fv3y: '#00f4ff',
    fv5y: '#7c3aed',
  };

  // Generate bezier path between two points with given widths
  const bezier = (x1, y1, x2, y2) => {
    const mid = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`;
  };

  // Build a flow ribbon polygon between two nodes with width = proportional
  const flowRibbon = (x1, y1, h1, x2, y2, h2) => {
    const mid = (x1 + x2) / 2;
    const topStart = y1 - h1 / 2;
    const botStart = y1 + h1 / 2;
    const topEnd = y2 - h2 / 2;
    const botEnd = y2 + h2 / 2;
    return `
      M ${x1} ${topStart}
      C ${mid} ${topStart}, ${mid} ${topEnd}, ${x2} ${topEnd}
      L ${x2} ${botEnd}
      C ${mid} ${botEnd}, ${mid} ${botStart}, ${x1} ${botStart}
      Z
    `;
  };

  // Animated particles along bezier paths — we use SVG animateMotion
  const Particle = ({ d, color, delay = 0, duration = 4, r = 3 }) => (
    <circle r={r} fill={color} style={{ filter: `drop-shadow(0 0 4px ${color})` }}>
      <animateMotion
        dur={`${duration}s`}
        repeatCount="indefinite"
        begin={`${delay}s`}
        path={d}
        rotate="auto"
      />
    </circle>
  );

  // Flow pair definitions
  const flows = useMemo(() => {
    const list = [
      // Capital → Baskets
      {
        id: 'cap-dca',
        from: { x: col.capital, y: H / 2, h: totalH * (dcaAlloc / 100) },
        to: { x: col.basket, y: dcaY, h: dcaH },
        color: COLORS.dca,
        amount: dcaInvested,
        label: `DCA ${dcaAlloc}%`,
      },
      {
        id: 'cap-ai',
        from: { x: col.capital, y: H / 2, h: totalH * (aiAlloc / 100) },
        to: { x: col.basket, y: aiY, h: aiH },
        color: COLORS.ai,
        amount: aiInvested,
        label: `AI ${aiAlloc}%`,
      },
      {
        id: 'cap-web3',
        from: { x: col.capital, y: H / 2, h: totalH * (web3Alloc / 100) },
        to: { x: col.basket, y: web3Y, h: web3H },
        color: COLORS.web3,
        amount: web3Invested,
        label: `Web3 ${web3Alloc}%`,
      },
    ];
    // AI Trading → bots
    if (aiAlloc > 0) {
      list.push(
        {
          id: 'ai-sigmai',
          from: { x: col.basket, y: aiY, h: sigmaiBotH },
          to: { x: col.bot, y: sigmaiY, h: sigmaiBotH },
          color: COLORS.sigmai,
          amount: sigmaiInvested,
          label: `Sigmai ${sigmaiPct}%`,
        },
        {
          id: 'ai-sugair',
          from: { x: col.basket, y: aiY, h: sugairBotH },
          to: { x: col.bot, y: sugairY, h: sugairBotH },
          color: COLORS.sugair,
          amount: sugairInvested,
          label: `Sugair ${sugairPct}%`,
        },
        {
          id: 'ai-jairvis',
          from: { x: col.basket, y: aiY, h: jairvisBotH },
          to: { x: col.bot, y: jairvisY, h: jairvisBotH },
          color: COLORS.jairvis,
          amount: jairvisInvested,
          label: `Jairvis ${jairvisPct}%`,
        }
      );
    }
    return list;
  }, [
    dcaAlloc,
    aiAlloc,
    web3Alloc,
    sigmaiPct,
    sugairPct,
    jairvisPct,
    totalH,
    dcaH,
    aiH,
    web3H,
    dcaY,
    aiY,
    web3Y,
    sigmaiY,
    sugairY,
    jairvisY,
    sigmaiBotH,
    sugairBotH,
    jairvisBotH,
    dcaInvested,
    aiInvested,
    web3Invested,
    sigmaiInvested,
    sugairInvested,
    jairvisInvested,
  ]);

  // Basket → FV flows (to 5y as main anchor, proportional gain)
  const basketToFvFlows = useMemo(() => {
    const basketGain = {
      dca: p5y.dcaCrypto || p5y.bitcoin || 0,
      ai: p5y.aiTrading || 0,
      web3: p5y.web3 || 0,
    };
    const totalBasket = basketGain.dca + basketGain.ai + basketGain.web3 || 1;
    return [
      {
        id: 'dca-fv',
        fromX: aiAlloc > 0 ? col.bot : col.basket,
        fromY: dcaY,
        fromH: dcaH * 0.7,
        color: COLORS.dca,
        contribution: basketGain.dca / totalBasket,
      },
      {
        id: 'ai-fv',
        fromX: col.bot,
        fromY: aiY,
        fromH: aiH * 0.7,
        color: COLORS.ai,
        contribution: basketGain.ai / totalBasket,
      },
      {
        id: 'web3-fv',
        fromX: aiAlloc > 0 ? col.bot : col.basket,
        fromY: web3Y,
        fromH: web3H * 0.7,
        color: COLORS.web3,
        contribution: basketGain.web3 / totalBasket,
      },
    ];
  }, [p5y, dcaY, aiY, web3Y, dcaH, aiH, web3H, aiAlloc]);

  // Node component
  const Node = ({ x, y, w = 14, h, color, glow = true }) => (
    <rect
      x={x - w / 2}
      y={y - h / 2}
      width={w}
      height={h}
      rx={w / 2}
      fill={color}
      style={glow ? { filter: `drop-shadow(0 0 8px ${color})` } : undefined}
    />
  );

  const TextTag = ({ x, y, children, color = '#fff', anchor = 'middle', size = 12, weight = 500 }) => (
    <text
      x={x}
      y={y}
      fill={color}
      textAnchor={anchor}
      fontSize={size}
      fontWeight={weight}
      style={{ pointerEvents: 'none' }}
    >
      {children}
    </text>
  );

  // Which basket is hovered for extra visibility
  const isActive = (id) => hovered === id || hovered === null;

  return (
    <Card
      className="bg-[#040816] border-[#0b1c38]"
      data-testid="capital-flow-card"
    >
      <CardHeader>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-[#00f4ff] flex items-center gap-2 text-lg">
              <Waves className="w-5 h-5" />
              {t('capitalFlowTitle')}
            </CardTitle>
            <p className="text-xs text-[#9EB4C0] mt-1 max-w-2xl">
              {t('capitalFlowSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-[#9EB4C0]">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#f7931a] shadow-[0_0_6px_#f7931a]"></span>
              DCA
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#00f4ff] shadow-[0_0_6px_#00f4ff]"></span>
              AI
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-[#12b7ff] shadow-[0_0_6px_#12b7ff]"></span>
              Web3
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="w-full overflow-x-auto">
          <svg
            viewBox={`0 0 ${W + PAD_X * 2} ${H + PAD_Y * 2}`}
            className="w-full"
            style={{ minWidth: '900px', maxHeight: '640px' }}
            data-testid="capital-flow-svg"
          >
            <defs>
              {/* Gradient for flow ribbons */}
              {Object.entries(COLORS).map(([k, c]) => (
                <linearGradient key={k} id={`grad-${k}`} x1="0%" x2="100%">
                  <stop offset="0%" stopColor={c} stopOpacity="0.5" />
                  <stop offset="100%" stopColor={c} stopOpacity="0.2" />
                </linearGradient>
              ))}
              {/* Glow filter */}
              <filter id="glow-soft">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <g transform={`translate(${PAD_X}, ${PAD_Y})`}>
              {/* Column labels */}
              <TextTag x={col.capital} y={16} color="#9EB4C0" size={11}>
                {t('flowColCapital')}
              </TextTag>
              <TextTag x={col.basket} y={16} color="#9EB4C0" size={11}>
                {t('flowColBaskets')}
              </TextTag>
              <TextTag x={col.bot} y={16} color="#9EB4C0" size={11}>
                {t('flowColAiSplit')}
              </TextTag>
              <TextTag x={col.fv} y={16} color="#9EB4C0" size={11}>
                {t('flowColFinalValue')}
              </TextTag>

              {/* Capital → Baskets ribbons */}
              {flows
                .filter((f) => f.id.startsWith('cap-'))
                .map((f) => (
                  <g
                    key={f.id}
                    onMouseEnter={() => setHovered(f.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={isActive(f.id) ? 1 : 0.25}
                  >
                    <path
                      d={flowRibbon(f.from.x, f.from.y, f.from.h, f.to.x, f.to.y, f.to.h)}
                      fill={`url(#grad-${
                        f.id === 'cap-dca' ? 'dca' : f.id === 'cap-ai' ? 'ai' : 'web3'
                      })`}
                      stroke={f.color}
                      strokeWidth={hovered === f.id ? 1.5 : 0.5}
                      strokeOpacity={0.6}
                    />
                  </g>
                ))}

              {/* AI → Bots ribbons */}
              {flows
                .filter((f) => f.id.startsWith('ai-'))
                .map((f) => (
                  <g
                    key={f.id}
                    onMouseEnter={() => setHovered(f.id)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: 'pointer' }}
                    opacity={isActive(f.id) ? 1 : 0.25}
                  >
                    <path
                      d={flowRibbon(f.from.x, f.from.y, f.from.h, f.to.x, f.to.y, f.to.h)}
                      fill={`url(#grad-${
                        f.id === 'ai-sigmai'
                          ? 'sigmai'
                          : f.id === 'ai-sugair'
                          ? 'sugair'
                          : 'jairvis'
                      })`}
                      stroke={f.color}
                      strokeWidth={hovered === f.id ? 1.5 : 0.5}
                      strokeOpacity={0.6}
                    />
                  </g>
                ))}

              {/* Baskets/bots → FV ribbons (to 1y/3y/5y) */}
              {basketToFvFlows.map((bf) =>
                [
                  { y: fvY1, alpha: 0.3, id: '1y' },
                  { y: fvY3, alpha: 0.55, id: '3y' },
                  { y: fvY5, alpha: 0.9, id: '5y' },
                ].map((fv) => {
                  const h = Math.max(6, bf.fromH * bf.contribution);
                  return (
                    <path
                      key={`${bf.id}-${fv.id}`}
                      d={flowRibbon(bf.fromX + 8, bf.fromY, h * 0.6, col.fv - 16, fv.y, 18 * bf.contribution + 4)}
                      fill={bf.color}
                      opacity={fv.alpha * 0.22}
                    />
                  );
                })
              )}

              {/* Capital node */}
              <Node x={col.capital} y={H / 2} h={totalH} color={COLORS.capital} />
              <TextTag x={col.capital} y={H / 2 - totalH / 2 - 14} color="#00f4ff" size={13} weight={700}>
                {formatCurrency(totalInvested)}
              </TextTag>
              <TextTag x={col.capital} y={H / 2 + totalH / 2 + 22} color="#9EB4C0" size={10}>
                {monthlyContribution > 0
                  ? `${formatCurrency(totalCapital)} + ${formatCurrency(
                      monthlyContribution
                    )}/mo`
                  : t('flowInitial')}
              </TextTag>

              {/* Basket nodes */}
              <Node x={col.basket} y={dcaY} h={dcaH} color={COLORS.dca} />
              <TextTag x={col.basket + 16} y={dcaY - 4} color={COLORS.dca} size={12} weight={700} anchor="start">
                ₿ {t('dcaCrypto')}
              </TextTag>
              <TextTag x={col.basket + 16} y={dcaY + 12} color="#9EB4C0" size={10} anchor="start">
                {dcaAlloc}% · {formatCurrency(dcaInvested)}
              </TextTag>

              <Node x={col.basket} y={aiY} h={aiH} color={COLORS.ai} />
              <TextTag x={col.basket + 16} y={aiY - 4} color={COLORS.ai} size={12} weight={700} anchor="start">
                🤖 {t('aiTrading')}
              </TextTag>
              <TextTag x={col.basket + 16} y={aiY + 12} color="#9EB4C0" size={10} anchor="start">
                {aiAlloc}% · {formatCurrency(aiInvested)}
              </TextTag>

              <Node x={col.basket} y={web3Y} h={web3H} color={COLORS.web3} />
              <TextTag x={col.basket + 16} y={web3Y - 4} color={COLORS.web3} size={12} weight={700} anchor="start">
                🌐 {t('web3')}
              </TextTag>
              <TextTag x={col.basket + 16} y={web3Y + 12} color="#9EB4C0" size={10} anchor="start">
                {web3Alloc}% · {formatCurrency(web3Invested)}
              </TextTag>

              {/* AI bot nodes */}
              {aiAlloc > 0 && (
                <>
                  <Node x={col.bot} y={sigmaiY} h={sigmaiBotH} color={COLORS.sigmai} />
                  <TextTag x={col.bot + 14} y={sigmaiY + 4} color={COLORS.sigmai} size={11} anchor="start">
                    Sigmai {sigmaiPct}%
                  </TextTag>
                  <Node x={col.bot} y={sugairY} h={sugairBotH} color={COLORS.sugair} />
                  <TextTag x={col.bot + 14} y={sugairY + 4} color={COLORS.sugair} size={11} anchor="start">
                    Sugair {sugairPct}%
                  </TextTag>
                  <Node x={col.bot} y={jairvisY} h={jairvisBotH} color={COLORS.jairvis} />
                  <TextTag x={col.bot + 14} y={jairvisY + 4} color={COLORS.jairvis} size={11} anchor="start">
                    Jairvis {jairvisPct}%
                  </TextTag>
                </>
              )}

              {/* FV nodes */}
              {[
                { y: fvY1, label: '1Y', value: fv1y, color: COLORS.fv1y },
                { y: fvY3, label: '3Y', value: fv3y, color: COLORS.fv3y },
                { y: fvY5, label: '5Y', value: fv5y, color: COLORS.fv5y },
              ].map((fv) => (
                <g key={fv.label}>
                  <rect
                    x={col.fv - 48}
                    y={fv.y - 30}
                    width={96}
                    height={60}
                    rx={12}
                    fill="#0b1c38"
                    stroke={fv.color}
                    strokeWidth={1.5}
                    style={{ filter: `drop-shadow(0 0 12px ${fv.color}80)` }}
                  />
                  <TextTag x={col.fv} y={fv.y - 10} color={fv.color} size={11} weight={700}>
                    {fv.label} · FV
                  </TextTag>
                  <TextTag x={col.fv} y={fv.y + 14} color="#fff" size={15} weight={800}>
                    {formatCurrency(fv.value)}
                  </TextTag>
                </g>
              ))}

              {/* Animated particles flowing through each ribbon */}
              {flows.map((f, idx) => {
                const d = bezier(f.from.x, f.from.y, f.to.x, f.to.y);
                const active = isActive(f.id);
                return (
                  <g key={`p-${f.id}`} opacity={active ? 1 : 0.15}>
                    <Particle d={d} color={f.color} delay={idx * 0.3} duration={3.5} r={2.5} />
                    <Particle d={d} color={f.color} delay={idx * 0.3 + 1.2} duration={3.5} r={2} />
                    <Particle d={d} color={f.color} delay={idx * 0.3 + 2.2} duration={3.5} r={1.5} />
                  </g>
                );
              })}

              {/* Particles from bots/baskets to 5y FV (most intense) */}
              {basketToFvFlows.map((bf, idx) => {
                const d = bezier(bf.fromX + 8, bf.fromY, col.fv - 16, fvY5);
                return (
                  <g key={`pfv-${bf.id}`}>
                    <Particle d={d} color={bf.color} delay={idx * 0.4} duration={4.5} r={2} />
                    <Particle d={d} color={bf.color} delay={idx * 0.4 + 1.8} duration={4.5} r={2.5} />
                  </g>
                );
              })}

              {/* Hover tooltip */}
              {hovered && (
                <g>
                  {(() => {
                    const f = flows.find((x) => x.id === hovered);
                    if (!f) return null;
                    const tx = (f.from.x + f.to.x) / 2;
                    const ty = (f.from.y + f.to.y) / 2 - 28;
                    return (
                      <g style={{ pointerEvents: 'none' }}>
                        <rect
                          x={tx - 68}
                          y={ty - 18}
                          width={136}
                          height={36}
                          rx={8}
                          fill="#0b1c38"
                          stroke={f.color}
                          strokeWidth={1}
                          style={{ filter: `drop-shadow(0 0 8px ${f.color}90)` }}
                        />
                        <TextTag x={tx} y={ty - 3} color={f.color} size={11} weight={700}>
                          {f.label}
                        </TextTag>
                        <TextTag x={tx} y={ty + 11} color="#fff" size={12} weight={700}>
                          {formatCurrency(f.amount)}
                        </TextTag>
                      </g>
                    );
                  })()}
                </g>
              )}
            </g>
          </svg>
        </div>

        {/* Helper text */}
        <div className="mt-4 flex items-center justify-center gap-6 flex-wrap text-[11px] text-[#9EB4C0]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-8 h-1 rounded-full bg-gradient-to-r from-transparent via-[#00f4ff] to-transparent" />
            {t('flowHoverHint')}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default CapitalFlow;
